import { computed, effect, signal } from '@preact/signals-react';
import type {
  WebAgentConversationInfo,
  WebAgentConversationParticipant,
  WebConversationListItem,
} from '@/website/lib/chat-api';
import type { ActivityLiveDataset } from './activity-live-dataset';
import { ConversationActivity } from './conversation-activity';
import type { ConversationLiveDataset } from './conversation-live-dataset';
import { ConversationMessageThread } from './conversation-message-thread';
import type { ConversationRuntimeRef } from './types';
import {
  isListedConversationKind,
  normalizeText,
} from './conversation-utils';

function emptyConversationInfo(
  conversationId: string
): WebAgentConversationInfo {
  return {
    conversationId,
    agentGroupId: null,
    kind: '',
    title: '',
    status: '',
    createdByAgentId: '',
    createdBySessionId: '',
    participants: [],
    messageCount: 0,
    targetMessageCount: 0,
    createdAt: 0,
    updatedAt: 0,
  };
}

function runtimeParticipantsForConversation(
  conversation: WebAgentConversationInfo
): WebAgentConversationParticipant[] {
  const participants = conversation.participants ?? [];
  return participants.filter(
    participant =>
      normalizeText(participant.actorType) === 'agent_session' &&
      normalizeText(participant.agentId) &&
      normalizeText(participant.sessionId)
  );
}

export function runtimeRefsForConversationInfo(
  conversation: WebAgentConversationInfo
): ConversationRuntimeRef[] {
  const conversationId = normalizeText(conversation.conversationId);
  if (!conversationId) return [];
  return runtimeParticipantsForConversation(conversation).map(participant => ({
    conversationId,
    agentId: normalizeText(participant.agentId) ?? '',
    sessionId: normalizeText(participant.sessionId) ?? '',
    participant,
  }));
}

export class Conversation {
  readonly id = signal(this.conversationId);
  readonly streamInfo = computed(() =>
    this.conversationData.conversationInfoFor(this.id.value)
  );
  readonly info = computed(
    () => this.streamInfo.value ?? emptyConversationInfo(this.conversationId)
  );
  readonly hasInfo = computed(() => Boolean(this.streamInfo.value));
  readonly deleted = computed(() =>
    this.conversationData.isDeleted(this.id.value)
  );
  readonly messages = new ConversationMessageThread(
    this.conversationId,
    this.conversationData
  );
  readonly localProcessing = signal(false);

  readonly participants = computed(() => this.info.value.participants ?? []);
  readonly isListed = computed(
    () => this.hasInfo.value && isListedConversationKind(this.info.value.kind)
  );
  readonly isTask = computed(
    () =>
      this.hasInfo.value && normalizeText(this.info.value.kind) === 'agent_task'
  );
  readonly canAcceptUserMessage = computed(
    () => this.hasInfo.value && !this.deleted.value
  );
  readonly isReadOnly = computed(() => !this.canAcceptUserMessage.value);
  readonly listItem = computed<WebConversationListItem>(() => ({
    conversationId: this.info.value.conversationId,
    agentGroupId: this.info.value.agentGroupId,
    kind: this.info.value.kind,
    title: this.info.value.title,
    createdByAgentId: this.info.value.createdByAgentId,
    createdBySessionId: this.info.value.createdBySessionId,
    createdAt: this.info.value.createdAt,
    updatedAt: this.info.value.updatedAt || this.info.value.createdAt,
    messageCount: this.info.value.messageCount,
    targetMessageCount: this.info.value.targetMessageCount,
    participants: this.participants.value,
  }));
  readonly runtimeRefs = computed<ConversationRuntimeRef[]>(() =>
    runtimeRefsForConversationInfo(this.info.value)
  );
  readonly runtimeRef = computed<ConversationRuntimeRef | null>(() => {
    const refs = this.runtimeRefs.value;
    return refs.length === 1 ? refs[0] : null;
  });
  readonly activity = new ConversationActivity(
    this.id,
    this.participants,
    this.activityData
  );
  private hasAppliedDelete = false;
  private readonly disposeDeletedEffect = effect(() => {
    if (this.deleted.value) {
      if (!this.hasAppliedDelete) {
        this.markDeleted();
        this.hasAppliedDelete = true;
      }
      return;
    }
    this.hasAppliedDelete = false;
  });

  constructor(
    private readonly conversationId: string,
    private readonly conversationData: ConversationLiveDataset,
    private readonly activityData: ActivityLiveDataset
  ) {}

  static shell(
    conversationId: string,
    conversationData: ConversationLiveDataset,
    activityData: ActivityLiveDataset
  ) {
    return new Conversation(conversationId, conversationData, activityData);
  }

  startLocalProcessing() {
    this.localProcessing.value = true;
  }

  finishLocalProcessing() {
    this.localProcessing.value = false;
  }

  markDeleted() {
    this.messages.reset();
    this.finishLocalProcessing();
  }

  dispose() {
    this.disposeDeletedEffect();
  }
}
